import { BasicResponse } from '../model/basic-response';

type ApiResponse = BasicResponse | Record<string, unknown> | null | undefined;

/** True when API indicates success (status/statusCode 200, errorNo 0, or errors[0].code is 0). 15/09/2026 PK. */
export function isBasicResponseSuccess(response: ApiResponse): boolean {
  if (!response) {
    return false;
  }
  const r = response as Record<string, unknown>;
  const status = r['status'];
  const statusCode = r['statusCode'];
  if (String(status) === '200' || statusCode === 200 || String(statusCode) === '200') {
    const err = (response as BasicResponse)?.errors?.[0];
    if (err && err.code !== 0 && String(err.code) !== '0' && (err.message || err.detail)) {
      return false;
    }
    const errorCode = r['errorCode'];
    if (
      errorCode !== undefined &&
      errorCode !== 0 &&
      String(errorCode) !== '0'
    ) {
      return false;
    }
    return true;
  }
  // AMASS /tem/updatetemplate returns { errorNo: 0, message: '...' }. 02/10/2026 HE
  const errorNo = r['errorNo'];
  if (errorNo !== undefined) {
    return errorNo === 0 || String(errorNo) === '0';
  }
  const code = (response as BasicResponse)?.errors?.[0]?.code;
  return code === 0 || String(code) === '0';
}

/**
 * User-facing text from a BasicResponse (never JSON.stringify the whole object).
 */
export function basicResponseUserMessage(
  response: ApiResponse,
  successFallback = 'Operation completed successfully',
  failureFallback = 'Operation failed',
): string {
  if (!response) {
    return failureFallback;
  }
  const basic = response as BasicResponse;
  if (basic.summary?.trim()) {
    return basic.summary.trim();
  }
  const topMessage = (response as Record<string, unknown>)['message'];
  if (typeof topMessage === 'string' && topMessage.trim()) {
    return topMessage.trim();
  }
  const errMsg = basic.errors?.[0]?.message?.trim();
  if (errMsg) {
    return errMsg;
  }
  const detail = basic.errors?.[0]?.detail?.trim();
  if (detail) {
    return detail;
  }
  return isBasicResponseSuccess(response) ? successFallback : failureFallback;
}

/** Failure message from API response body. 15/09/2026 PK. */
export function getBasicResponseErrorMessage(
  response: ApiResponse,
  fallback = 'Operation failed',
): string {
  return basicResponseUserMessage(response, fallback, fallback);
}

/** User-friendly HTTP error text (not raw HttpClient failure string). 15/09/2026 PK. */
export function formatHttpErrorMessage(err: unknown, fallback = 'Request failed'): string {
  const e = err as {
    error?: { message?: string; errors?: Array<{ message?: string; detail?: string }> };
    message?: string;
    status?: number;
    statusText?: string;
  };
  if (e?.error && typeof e.error === 'object') {
    const fromBody = getBasicResponseErrorMessage(e.error as ApiResponse, '');
    if (fromBody) {
      return fromBody;
    }
    if (typeof e.error === 'string') {
      return e.error;
    }
  }
  const msg = e?.message ?? '';
  if (msg.startsWith('Http failure response')) {
    if (e?.statusText) {
      return e.statusText;
    }
    const match = msg.match(/: (\d+ .+)$/);
    if (match?.[1]) {
      return match[1];
    }
    return fallback;
  }
  return msg || fallback;
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Treat AMASS { errorNo: 0, message } as success and show message. What changed: isBasicResponseSuccess() returns true when errorNo is 0/'0'; basicResponseUserMessage() uses top-level message when summary is empty. Previous behavior: Template update body {"errorNo":0,"message":"Template Updated Successfully"} had no status/errors[0].code, so callers used the failure fallback "Update failed". New behavior: errorNo 0 is success and the banner shows the API message. Reason: QA Marcus Hattingh — Template edit success said Update Failed. Context: POST /tem/updatetemplate. Impact: All basicResponseUserMessage / isBasicResponseSuccess callers. - 2026-10-02 - HE

